/**
 * Entropy Friction Bars
 * Two stacked horizontal bars: Revenue Friction (REM) and Expense Friction (EEM).
 * Segment width = normalized contribution (1 - correction); color = entropy score.
 * @feature BRIDGE_SESSION_FLOW - Entropy heat map stacked bars
 */

import React, { useRef, useEffect, useMemo } from 'react';
import * as d3 from 'd3';
import { Tooltip } from 'antd';
import { REM_FACTORS, EEM_FACTORS, ENTROPY_FACTOR_LABELS } from '../../config/entropyHierarchy';
import styles from './EntropyFrictionBars.module.css';

export interface FactorData {
  entropy: number;
  correction: number;
  label: string;
}

export interface EntropyFrictionBarsProps {
  /** Factor ID -> { entropy, correction, label }. Built from hierarchy or scores. */
  scopeMap: Map<string, FactorData>;
  barHeight?: number;
  onSegmentClick?: (factorId: string, artifactItemId: string) => void;
  /** Total REM correction (Revenue Efficiency Metric) to display. */
  remCorrection?: number;
  /** Total EEM correction (Expense Efficiency Metric) to display. */
  eemCorrection?: number;
}

const getEntropyColor = (score: number): string => {
  if (score >= 7) return '#cf1322';
  if (score >= 5) return '#fa8c16';
  if (score >= 4) return '#fadb14';
  if (score >= 3) return '#95de64';
  return '#389e0d';
};

interface Segment {
  factorId: string;
  artifactItemId: string;
  label: string;
  entropy: number;
  correction: number;
  friction: number;
  contributionPct: number;
  width: number;
  color: string;
}

function buildSegments(
  factorIds: readonly string[],
  scopeMap: Map<string, FactorData>
): Segment[] {
  const segments: Segment[] = [];
  let totalFriction = 0;

  for (const factorId of factorIds) {
    const data = scopeMap.get(factorId);
    if (!data) continue;

    const correction = Math.max(0, Math.min(1, data.correction ?? 0));
    const friction = 1 - correction;
    totalFriction += friction;

    segments.push({
      factorId,
      artifactItemId: `item-${factorId}`,
      label: data.label || ENTROPY_FACTOR_LABELS[factorId] || factorId,
      entropy: data.entropy,
      correction,
      friction,
      contributionPct: 0,
      width: 0,
      color: getEntropyColor(data.entropy),
    });
  }

  // Normalize widths
  const divisor = totalFriction > 0 ? totalFriction : segments.length;
  segments.forEach((s) => {
    s.contributionPct = divisor > 0 ? ((s.friction > 0 ? s.friction : 1) / divisor) * 100 : 0;
    s.width = s.contributionPct / 100;
  });

  return segments;
}

export const EntropyFrictionBars: React.FC<EntropyFrictionBarsProps> = ({
  scopeMap,
  barHeight = 24,
  onSegmentClick,
  remCorrection,
  eemCorrection,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const { revenueSegments, expenseSegments } = useMemo(() => {
    const revenue = buildSegments(REM_FACTORS, scopeMap);
    const expense = buildSegments(EEM_FACTORS, scopeMap);
    return { revenueSegments: revenue, expenseSegments: expense };
  }, [scopeMap]);

  const hasData = revenueSegments.length > 0 || expenseSegments.length > 0;

  useEffect(() => {
    if (!svgRef.current || !containerRef.current || !hasData) return;

    const container = containerRef.current;
    const svgEl = svgRef.current;

    const animationFrameId = requestAnimationFrame(() => {
      if (!svgEl || !containerRef.current) return;
      const svg = d3.select(svgEl);
      svg.selectAll('*').remove();

      const labelWidth = remCorrection != null || eemCorrection != null ? 220 : 140;
      const margin = { top: 8, right: 8, bottom: 8, left: labelWidth };
      const width = Math.max(container.clientWidth, 200);
      const innerWidth = width - margin.left - margin.right;
      const barGap = 16;
      const revenueBarY = 0;
      const expenseBarY = revenueBarY + barHeight + barGap;

      const g = svg
        .attr('width', width)
        .attr('height', expenseBarY + barHeight + margin.bottom)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      const drawBar = (segments: Segment[], barY: number) => {
        let offset = 0;
        segments.forEach((seg) => {
          if (seg.width <= 0) return;
          const segWidth = seg.width * innerWidth;
          const rect = g
            .append('g')
            .attr('data-factor-id', seg.factorId)
            .style('cursor', onSegmentClick ? 'pointer' : 'default');
          rect
            .append('rect')
            .attr('x', offset)
            .attr('y', barY)
            .attr('width', Math.max(2, segWidth))
            .attr('height', barHeight)
            .attr('fill', seg.color)
            .attr('stroke', '#000')
            .attr('stroke-width', 1)
            .attr('rx', 2);
          rect
            .append('title')
            .text(
              `${seg.label}: entropy ${seg.entropy}/10, correction ${seg.correction.toFixed(2)}, contribution ${seg.contributionPct.toFixed(1)}%`
            );
          offset += segWidth;
        });
      };

      // Revenue bar label (with optional REM correction)
      const revenueLabel = remCorrection != null
        ? `Revenue Friction (REM: ${remCorrection.toFixed(2)})`
        : 'Revenue Friction';
      g.append('text')
        .attr('x', -8)
        .attr('y', revenueBarY + barHeight / 2)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .style('font-size', '12px')
        .style('fill', '#595959')
        .text(revenueLabel);

      drawBar(revenueSegments, revenueBarY);

      // Expense bar label (with optional EEM correction)
      const expenseLabel = eemCorrection != null
        ? `Expense Friction (EEM: ${eemCorrection.toFixed(2)})`
        : 'Expense Friction';
      g.append('text')
        .attr('x', -8)
        .attr('y', expenseBarY + barHeight / 2)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .style('font-size', '12px')
        .style('fill', '#595959')
        .text(expenseLabel);

      drawBar(expenseSegments, expenseBarY);

      // Click handlers
      if (onSegmentClick) {
        g.selectAll('g[data-factor-id]').on('click', function () {
          const factorId = d3.select(this).attr('data-factor-id');
          if (factorId) onSegmentClick(factorId, `item-${factorId}`);
        });
      }
    });

    return () => cancelAnimationFrame(animationFrameId);
  }, [revenueSegments, expenseSegments, barHeight, hasData, onSegmentClick, remCorrection, eemCorrection]);

  if (!hasData) return null;

  return (
    <div className={styles.barsWrap}>
      <div className={styles.barsLabel}>Friction by factor</div>
      <div ref={containerRef} className={styles.barsContainer}>
        <svg ref={svgRef} />
      </div>
      <div className={styles.legends}>
        {[...revenueSegments, ...expenseSegments].map((seg) => (
          <Tooltip
            key={seg.factorId}
            title={
              <div>
                <div><strong>{seg.label}</strong></div>
                <div>Entropy: {seg.entropy}/10</div>
                <div>Correction: {seg.correction.toFixed(2)}</div>
                <div>Contribution: {seg.contributionPct.toFixed(1)}%</div>
              </div>
            }
          >
            <span className={styles.legendItem}>
              <span
                className={styles.legendSwatch}
                style={{ backgroundColor: seg.color }}
              />
              {seg.label}
            </span>
          </Tooltip>
        ))}
      </div>
    </div>
  );
};

export default EntropyFrictionBars;
